import { useEffect, useRef } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { LoginBrand } from '../components/auth/LoginBrand';
import { VerifyingOverlay } from '../components/ui/VerifyingOverlay';
import { hasAuthCallbackInUrl } from '../lib/authBootstrap';
import { clearAuthCallbackParams } from '../lib/authErrors';
import { formatAuthErrorMessage } from '../lib/formatAuthError';
import {
  consumePostLoginReturn,
  IS_UNIFIED_PORTAL,
  portalHomePath,
  readPortalChoice,
} from '../lib/portal';
import { resolveDefaultPortalKind, type PortalAccessMap } from '../lib/portalAccess';

function defaultPostAuthDestination(portalAccess: PortalAccessMap | null): string {
  if (IS_UNIFIED_PORTAL) {
    const choice =
      readPortalChoice() ?? (portalAccess ? resolveDefaultPortalKind(portalAccess) : null);
    return choice ? portalHomePath(choice) : '/choose-portal';
  }
  return '/';
}

function CallbackWait({
  message,
  authError,
}: {
  message: string;
  authError?: string | null;
}) {
  return (
    <div className="login-page">
      <div className="login-card">
        <LoginBrand />
        <div className="login-card__body">
          <p className="login-card__lead">Sign in with your company Microsoft account</p>
          {authError ? (
            <div className="alert alert-error">{formatAuthErrorMessage(authError)}</div>
          ) : null}
        </div>
      </div>
      <VerifyingOverlay message={message} />
    </div>
  );
}

export function AuthCallbackPage() {
  const { session, loading, authError, clearAuthError, accessStatus, refreshProfile, portalAccess } =
    useAuth();
  const navigate = useNavigate();
  const destinationRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loading && authError) {
      clearAuthCallbackParams();
    }
  }, [loading, authError]);

  useEffect(() => {
    if (loading || !session) return;

    if (accessStatus === 'not_on_allowlist' || accessStatus === 'no_email') {
      navigate('/not-authorized', { replace: true });
      return;
    }
    if (accessStatus === 'choose_portal') {
      navigate('/choose-portal', { replace: true });
      return;
    }
    if (accessStatus === 'allowed') {
      if (!destinationRef.current) {
        destinationRef.current =
          consumePostLoginReturn() ?? defaultPostAuthDestination(portalAccess);
      }
      navigate(destinationRef.current, { replace: true });
    }
  }, [loading, session, navigate, accessStatus, portalAccess]);

  if (loading || (hasAuthCallbackInUrl() && !session)) {
    return <CallbackWait message="Completing Microsoft sign-in…" authError={authError} />;
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (accessStatus === 'checking' || (accessStatus === 'idle' && !authError)) {
    return <CallbackWait message="Verifying access…" authError={authError} />;
  }

  if (accessStatus === 'not_on_allowlist' || accessStatus === 'no_email') {
    return <Navigate to="/not-authorized" replace />;
  }

  if (accessStatus === 'choose_portal') {
    return <Navigate to="/choose-portal" replace />;
  }

  if (accessStatus === 'allowed') {
    if (!destinationRef.current) {
      destinationRef.current =
        consumePostLoginReturn() ?? defaultPostAuthDestination(portalAccess);
    }
    return <Navigate to={destinationRef.current} replace />;
  }

  if (accessStatus === 'idle' && authError) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>Sign-in failed</h1>
          <div className="alert alert-error">{formatAuthErrorMessage(authError)}</div>
          <div className="login-page__actions">
            <button type="button" className="btn-primary" onClick={() => void refreshProfile()}>
              Retry
            </button>
            <Link
              to="/login"
              className="btn-secondary"
              style={{ textDecoration: 'none' }}
              onClick={() => clearAuthError()}
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return <CallbackWait message="Verifying access…" authError={authError} />;
}
